import { checkRemoteSignRequest, checkRemoteSignResponse, REMOTE_SIGN_PROTOCOL_VERSION, type RemoteSignResult } from '../../supabase/functions/_shared/remoteSignProtocol';
import { handleRemoteSign, type RemoteSignStore, type SignRow } from '../../supabase/functions/sign-v2/core/service';
import type { RemoteSignTransport } from './remoteSignClient';

// DEVELOPMENT ONLY (src/backend/remoteSignClient.ts selects it only when import.meta.env.DEV and the web-version
// switch is on): the REAL sign-v2 service over a table kept in this browser's localStorage, so the inspector's
// side and the client's /sign/<token> page can be walked end to end in the dev server (two tabs share it).
// Never in a build.
const KEY = 'yanwushi-dev:remote-sign-table';

type StoredRow = SignRow & { tokenHash: string };

function load(): StoredRow[] {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) ?? '[]') as StoredRow[];
  } catch {
    return [];
  }
}

function save(rows: StoredRow[]): void {
  window.localStorage.setItem(KEY, JSON.stringify(rows));
}

const store: RemoteSignStore = {
  async insert(row) {
    const id = crypto.randomUUID();
    save([...load(), { id, userId: row.userId, inspectionId: row.inspectionId, tokenHash: row.tokenHash, documentHtml: row.documentHtml, facts: row.facts, locale: row.locale, expiresAt: row.expiresAt, signedAt: null, result: null }]);
    return id;
  },
  async deleteForInspection(userId, inspectionId) {
    save(load().filter((r) => !(r.userId === userId && r.inspectionId === inspectionId)));
  },
  async byTokenHash(hash) {
    return load().find((r) => r.tokenHash === hash) ?? null;
  },
  async byId(id, userId) {
    return load().find((r) => r.id === id && r.userId === userId) ?? null;
  },
  async markSigned(id, result: RemoteSignResult) {
    const rows = load();
    const row = rows.find((r) => r.id === id);
    if (!row || row.signedAt !== null) return false;
    save(rows.map((r) => (r.id === id ? { ...r, signedAt: result.signedAt, result, documentHtml: '' } : r)));
    return true;
  },
  async remove(id, userId) {
    save(load().filter((r) => !(r.id === id && r.userId === userId)));
  }
};

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export const devRemoteSignTransport: RemoteSignTransport = {
  async call(request, token) {
    const checked = checkRemoteSignRequest({ protocolVersion: REMOTE_SIGN_PROTOCOL_VERSION, ...request });
    if (!checked.ok) return { kind: 'failed', code: 'badRequest' };
    // The development scenarios have no real session: the inspector's actions run as one development account.
    const inspectorAction = request.action === 'create' || request.action === 'status' || request.action === 'discard';
    void token;
    const response = await handleRemoteSign(checked.request, { userId: inspectorAction ? 'dev-inspector' : null, ip: '127.0.0.1', userAgent: navigator.userAgent }, { store, now: () => Date.now(), randomToken, sha256Hex });
    const parsed = checkRemoteSignResponse(JSON.parse(JSON.stringify(response)));
    if (!parsed) return { kind: 'failed', code: 'internalError' };
    return parsed.ok ? { kind: 'ok', response: parsed } : { kind: 'failed', code: parsed.code };
  }
};
